import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Stack,
  alpha,
  styled,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload,
  Close as CloseIcon,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { minioService } from '../../api/services/minioService';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const DropZone = styled(Box)(({ theme, isDragging, isError }) => ({
  border: `2px dashed ${isError ? '#EF4444' : isDragging ? '#2469F0' : '#D0D5DD'}`,
  borderRadius: '12px',
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: isDragging ? alpha('#2469F0', 0.04) : 'transparent',
  '&:hover': {
    borderColor: '#2469F0',
    backgroundColor: alpha('#2469F0', 0.02),
  },
}));

const FilePreview = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  borderRadius: '8px',
  backgroundColor: '#F8FAFC',
  border: '1px solid #E2E8F0',
  marginTop: theme.spacing(2),
}));

const ALLOWED_TYPES = [
  '.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif',
  '.xls', '.xlsx', '.csv', '.txt', '.zip', '.rar',
];

const MAX_FILE_SIZE = 20 * 1024 * 1024;

const DocumentUploader = ({
  onUploadSuccess,
  onUploadError,
  onFileSelect,
  onUploadStart,
  onUploadComplete,
  accept = ALLOWED_TYPES.join(','),
  maxSize = MAX_FILE_SIZE,
  folder = 'uploads',
  multiple = false,
  buttonText = 'Upload File',
  dropZoneText = 'Drag & drop files here, or click to browse',
  showDropZone = true,
  showPreview = true,
  autoUpload = false,
  uploadLabel = 'Uploading...',
  successLabel = 'Uploaded Successfully',
  errorLabel = 'Upload Failed',
  variant = 'button',
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = (file) => {
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    const allowedTypes = accept.split(',').map(t => t.trim().toLowerCase());
    
    if (!allowedTypes.includes(fileExt)) {
      return {
        valid: false,
        error: `File type "${fileExt}" is not allowed. Allowed types: ${accept}`,
      };
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum allowed size (${(maxSize / 1024 / 1024).toFixed(0)}MB)`,
      };
    }

    return { valid: true };
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
    e.target.value = '';
  };

  const processFiles = (files) => {
    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    });

    if (errors.length > 0) {
      errors.forEach((err) => enqueueSnackbar(err, { variant: 'error' }));
    }

    if (validFiles.length === 0) return;

    if (!multiple) {
      setSelectedFiles([validFiles[0]]);
      if (onFileSelect) onFileSelect(validFiles[0]);
      if (autoUpload) handleUpload([validFiles[0]]);
      return;
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    if (onFileSelect) onFileSelect(validFiles);
    if (autoUpload) handleUpload(validFiles);
  };

  const handleUpload = async (files = selectedFiles) => {
    if (files.length === 0) return;

    setUploading(true);
    if (onUploadStart) onUploadStart();

    const results = [];
    const fileList = Array.isArray(files) ? files : [files];

    for (const file of fileList) {
      const fileId = file.name + file.size;
      setUploadProgress((prev) => ({ ...prev, [fileId]: 0 }));
      setUploadStatus((prev) => ({ ...prev, [fileId]: 'uploading' }));

      try {
        const response = await minioService.uploadFile(file, folder);

        if (response.success) {
          setUploadStatus((prev) => ({ ...prev, [fileId]: 'success' }));
          setUploadProgress((prev) => ({ ...prev, [fileId]: 100 }));

          const result = {
            success: true,
            file: file,
            url: response.data.url,
            filename: response.data.filename,
            file_size: response.data.file_size,
            content_type: response.data.content_type,
            folder: response.data.folder,
            uploaded_at: response.data.uploaded_at,
          };
          results.push(result);

          if (onUploadSuccess) onUploadSuccess(result);
        } else {
          throw new Error(response.message || 'Upload failed');
        }
      } catch (error) {
        setUploadStatus((prev) => ({ ...prev, [fileId]: 'error' }));
        const errorMsg = error.message || 'Failed to upload file';
        if (onUploadError) onUploadError(errorMsg, file);
        enqueueSnackbar(`Failed to upload ${file.name}: ${errorMsg}`, { variant: 'error' });
        results.push({ success: false, file, error: errorMsg });
      }
    }

    setUploading(false);
    if (onUploadComplete) onUploadComplete(results);

    if (!multiple && results.length > 0 && results[0].success) {
      setSelectedFiles([]);
    }
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearAll = () => {
    setSelectedFiles([]);
    setUploadStatus({});
    setUploadProgress({});
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const formatBytes = (bytes) => {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase() || '';
    if (['pdf'].includes(ext)) return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return '🖼️';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
    if (['zip', 'rar'].includes(ext)) return '📦';
    return '📎';
  };

  const renderDropZone = () => {
    if (!showDropZone) return null;

    return (
      <DropZone
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        isDragging={isDragging}
        isError={selectedFiles.some(f => uploadStatus[f.name + f.size] === 'error')}
      >
        <CloudUpload sx={{ fontSize: 48, color: '#94A3B8', mb: 2 }} />
        <Typography variant="body1" color="#475569">
          {dropZoneText}
        </Typography>
        <Typography variant="caption" color="#94A3B8">
          Allowed: {accept} • Max: {(maxSize / 1024 / 1024).toFixed(0)}MB
        </Typography>
      </DropZone>
    );
  };

  const renderFilePreview = () => {
    if (!showPreview || selectedFiles.length === 0) return null;

    return (
      <Box>
        {selectedFiles.map((file, index) => {
          const fileId = file.name + file.size;
          const status = uploadStatus[fileId];
          const progress = uploadProgress[fileId] || 0;

          return (
            <FilePreview key={index}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                <Box sx={{ fontSize: 24 }}>{getFileIcon(file.name)}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="#94A3B8">
                    {formatBytes(file.size)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {status === 'uploading' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={24} value={progress} variant="determinate" />
                    <Typography variant="caption" color="#94A3B8">
                      {Math.round(progress)}%
                    </Typography>
                  </Box>
                )}

                {status === 'success' && (
                  <Tooltip title="Uploaded successfully">
                    <CheckCircle sx={{ color: '#10B981', fontSize: 20 }} />
                  </Tooltip>
                )}

                {status === 'error' && (
                  <Tooltip title="Upload failed">
                    <ErrorIcon sx={{ color: '#EF4444', fontSize: 20 }} />
                  </Tooltip>
                )}

                {!status && !uploading && (
                  <Tooltip title="Remove file">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      sx={{ color: '#94A3B8' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </FilePreview>
          );
        })}
      </Box>
    );
  };

  const renderUploadButton = () => {
    if (variant === 'dropzone') return null;

    return (
      <Button
        component="label"
        variant="contained"
        startIcon={<CloudUpload />}
        disabled={uploading}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '8px',
          bgcolor: '#2469F0',
          '&:hover': {
            bgcolor: '#1A5BD4',
            boxShadow: '0px 4px 12px rgba(36, 105, 240, 0.3)',
          },
          '&.Mui-disabled': {
            bgcolor: '#F1F5F9',
            color: '#94A3B8',
          },
        }}
      >
        {uploading ? uploadLabel : buttonText}
        <VisuallyHiddenInput
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileSelect}
        />
      </Button>
    );
  };

  const renderUploadAction = () => {
    if (autoUpload || selectedFiles.length === 0) return null;

    const hasUploading = Object.values(uploadStatus).some(s => s === 'uploading');
    const hasSuccess = Object.values(uploadStatus).some(s => s === 'success');
    const allDone = selectedFiles.every(f => uploadStatus[f.name + f.size]);

    if (allDone && hasSuccess) {
      return (
        <Button
          variant="outlined"
          onClick={handleClearAll}
          sx={{ textTransform: 'none', color: '#64748B' }}
        >
          Clear All
        </Button>
      );
    }

    return (
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          onClick={() => handleUpload(selectedFiles)}
          disabled={uploading || hasUploading}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            bgcolor: '#2469F0',
            '&:hover': {
              bgcolor: '#1A5BD4',
            },
          }}
        >
          {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''}`}
        </Button>
        <Button
          variant="outlined"
          onClick={handleClearAll}
          disabled={uploading}
          sx={{ textTransform: 'none', color: '#64748B' }}
        >
          Clear All
        </Button>
      </Stack>
    );
  };

  return (
    <Box>
      {variant === 'button' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {renderUploadButton()}
          {renderUploadAction()}
        </Box>
      )}

      {variant === 'dropzone' && (
        <Box>
          {renderDropZone()}
          {renderFilePreview()}
        </Box>
      )}

      {variant === 'both' && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {renderUploadButton()}
            {renderUploadAction()}
          </Box>
          {renderDropZone()}
          {renderFilePreview()}
        </Box>
      )}

      {variant === 'button' && renderFilePreview()}
    </Box>
  );
};

export default DocumentUploader;